import { useEffect, useMemo, useRef, useState } from 'react';

import { VpIcon } from '@vtmn-play/react';
import clsx from 'clsx';

import Snackbar from '../Snackbar';
import uuid from '../../utils/uuid/uuid';

import { AlertProps } from './Alert.props';

import './Alert.scss';

const Alert = ({
  className,
  closeButtonLabel,
  content,
  id,
  onClose,
  onExited,
  permanent,
  timeout,
  title,
  variant,
}: AlertProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(true);

  const delayBeforeClosing = timeout ?? 7500;
  const snackbarRef = useRef<HTMLDialogElement>(null);

  // clearAlert fires when closing animation ends (snackbar is then unmounted)
  const clearAlert = () => {
    if (onExited) {
      onExited();
    }
  };

  // closeAlert fires when closing animation is asked
  // (ie. timer comes to an end or close button is clicked)
  const closeAlert = () => {
    setIsOpen(false);

    if (onClose) {
      onClose();
    }
  };

  // We only want to execute closeAlert for this specific animation end
  const closeAlertAfterAnimation = useMemo(
    () => (e: AnimationEvent) => {
      if (e?.animationName === 'alert-timer') {
        closeAlert();
      }
    },
    [],
  );

  // Esc key
  const handleEsc = useMemo(
    () => (e: KeyboardEvent) => {
      if (e?.key === 'Escape') {
        closeAlert();
      }
    },
    [],
  );

  useEffect(() => {
    if (!permanent) {
      snackbarRef.current?.addEventListener(
        'animationend',
        closeAlertAfterAnimation,
      );
      snackbarRef?.current?.style.setProperty(
        '--closing-delay',
        `${delayBeforeClosing}ms`,
      );
    }
    document.addEventListener('keydown', handleEsc);

    return () => {
      snackbarRef.current?.addEventListener(
        'animationend',
        closeAlertAfterAnimation,
      );
      document.removeEventListener('keydown', handleEsc);
    };
  }, []);

  return (
    <Snackbar
      className={clsx(className, !permanent ? 'timed-alert' : undefined)}
      closeButtonLabel={closeButtonLabel ?? 'Close alert'}
      id={id ?? uuid()}
      isOpen={isOpen}
      onClose={closeAlert}
      onExited={clearAlert}
      ref={snackbarRef}
      title={
        <div
          className={clsx('alert-title', variant && `alert-title--${variant}`)}
        >
          {variant === 'success' && <VpIcon size={20} name='check' />}
          {variant === 'info' && <VpIcon size={20} name='information' />}
          {variant === 'danger' && <VpIcon size={20} name='error' />}
          {variant === 'warning' && <VpIcon size={20} name='warning-error' />}
          {title}
        </div>
      }
    >
      {content}
    </Snackbar>
  );
};

export default Alert;
